import {
  Button,
  Icon,
  TruncatedTooltip,
} from "@smart/itops-sb-design-system-dom";

import { getRoleContactWarning } from "./duplicate-helpers";
import { DuplicateWarningLine } from "./duplicate-warning";
import { GqlField, GqlGroup, GqlMatterLayout, MatterField } from "../../types";
import {
  ContactType,
  getLabelText,
  resolveStandardContactFields,
} from "../../utils";

export type RoleRowProps = {
  layout: GqlMatterLayout;
  matterFields: MatterField[];
  contactType: ContactType;
  isLoading: boolean;
  disabled?: boolean;
  formFields: GqlField[];
  formGroups: GqlGroup[];
  onAdd: (layout: GqlMatterLayout) => void;
  onCustomiseBeforeAdding: (layout: GqlMatterLayout) => void;
};

export const RoleRow = ({
  layout,
  matterFields,
  contactType,
  isLoading,
  disabled,
  formFields,
  formGroups,
  onAdd,
  onCustomiseBeforeAdding,
}: RoleRowProps) => {
  const standardFields = resolveStandardContactFields({
    matterFields,
    contactType,
  });
  const fieldLabels = standardFields
    .map((field) => getLabelText(field))
    .join(", ");
  const duplicateWarning =
    isLoading || !standardFields.length
      ? undefined
      : getRoleContactWarning({
          layoutId: layout.id,
          contactType,
          roleDisplayName: layout.displayName || layout.name,
          matterFields,
          formFields,
          formGroups,
        });
  const canAdd =
    !disabled && !isLoading && standardFields.length > 0 && !duplicateWarning;

  return (
    <div className="flex items-center gap-small px-[0.8rem] py-[1.2rem] rounded hover:bg-neutral-20">
      <div className="flex justify-center items-center w-[3.2rem] h-[3.2rem] rounded bg-blue-60">
        <Icon
          name={
            contactType === "company"
              ? "regularBuildingColumns"
              : "regularCircleUser"
          }
          className="text-blue-560"
        />
      </div>
      <div className="flex flex-col overflow-hidden flex-1 mr-[4rem]">
        <span className="font-semibold truncate">
          {layout.displayName || layout.name}
        </span>
        <div className="min-w-0 w-full">
          {duplicateWarning ? (
            <DuplicateWarningLine {...duplicateWarning} />
          ) : (
            <TruncatedTooltip
              textToDisplay={isLoading ? "Loading fields…" : fieldLabels}
              tooltipText={isLoading ? undefined : fieldLabels}
              className="text-caption text-neutral-400 truncate"
              placement="bottom"
              offsetBy={5}
            />
          )}
        </div>
      </div>
      <div className="flex items-center gap-small">
        <Button
          text="Add"
          leftIcon="regularPlus"
          variant="primarySubtle"
          size="small"
          disabled={!canAdd}
          onClick={() => onAdd(layout)}
        />
        <Button
          aria-label="customise fields before adding"
          size="small"
          variant="secondarySubtle"
          leftIcon="solidEllipsis"
          disabled={!canAdd}
          onClick={() => onCustomiseBeforeAdding(layout)}
        />
      </div>
    </div>
  );
};
